import { openAlertModal } from 'admin/actions/alert-modal';
import {
  createClearingOffset,
  deleteClearingOffset,
  getClearing,
  getClearings,
  updateClearings,
} from 'admin/actions/clearing';
import { updateListForm } from 'admin/actions/transactions';
import { canSettleClearing } from 'admin/authorities';
import Breadcrumb from 'admin/components/shared/Breadcrumb';
import { isNullOrUndifined, isPaymentClearing } from 'admin/utils';
import moment from 'moment';
import React, { Component } from 'react';
import CSSModules from 'react-css-modules';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { formatNum } from '~/javascripts/admin/utils';
import Button from '../../atoms/Buttons';
import Grid from '../../atoms/Grid';
import Headings from '../../atoms/Headings';
import Loader from '../../atoms/Loader';
import ClearingBadge from '../../shared/ClearingBadge';
import OffsetStatusBadge from '../../shared/OffsetStatusBadge';
import PaymentNoticeDownloadButton from '../../shared/PaymentNoticeDownloadButton';
import AmountCell from './AmountCell';
import ClearingCell from './ClearingCell';
import styles from './styles.scss';

// 上の「合計」セクション(GridWrapper)と左端・金額カラムを揃えるため、左マージンは付けない。
const OffsetSection = styled.div`
  margin: 16px 0 0;
`;

// 精算済化（不可逆）の導線。一覧と同じく発行体・未精算時のみ表示し、警告色（赤）にする。
const DetailSettleButton = styled.button`
  margin-left: 12px;
  border-radius: 4px;
  padding: 2px 10px;
  font-size: 0.875rem;
  color: #d75f48;
  border: 1px solid #d75f48;
  background: #ffffff;
  cursor: pointer;

  &:hover {
    background: #fdf3f1;
  }
`;

// 予定相殺付きの請求は精算不可。ボタンの代わりに理由と誘導を出す（下の「債権相殺」で相殺を外す）。
// 文言は途中で折り返さない（nowrap）。「未精算」ラベルと同じ行に一続きで表示する。
const PendingOffsetNote = styled.span`
  margin-left: 12px;
  font-size: 0.8125rem;
  color: #888888;
  white-space: nowrap;
`;

// 相殺リストが「相手側」精算データの一覧であることを明示する見出し（請求詳細では相手＝支払）。
const OffsetCaption = styled.p`
  margin: 12px 0 0;
  font-size: 0.8125rem;
  color: #666666;
`;

const OffsetTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 12px;

  th,
  td {
    border-bottom: 1px solid #eee;
    padding: 8px 12px;
    text-align: left;
    font-size: 13px;
  }

  th {
    color: #888;
    font-weight: 500;
  }

  td.amount {
    text-align: right;
  }
`;

// reactstrap の Modal は bootstrap の _modal.scss を要するが当 admin は未 import のため、
// styled-components で自前のモーダルを組む（レイアウト崩れ回避）。
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
`;

const ModalDialog = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 680px;
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #eee;
`;

const ModalCloseButton = styled.button`
  border: none;
  background: none;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #888;
`;

const ModalBodyWrap = styled.div`
  padding: 16px;
  overflow: auto;
`;

const ModalFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #eee;
`;

const PageWrapper = styled.div``;
const GridWrapper = styled(Grid.Container)``;
const SubHeading = styled(Headings.h6)`
  padding-left: 16px;
`;

const Cell = styled.div`
  display: flex;
  justify-content: end;
`;

const TransactionDetailButton = styled(Button)`
  margin-left: 24px;
  height: 30px;
  line-height: 20px;
  min-width: unset;
`;

const FormulaText = styled.div`
  margin-left: 24px;
`;

const LoaderWrapper = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DetailContent = styled.section``;

class ClearingDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      clearing: {},
      detail: {},
      offsets: [],
      offsetModalOpen: false,
      offsetCandidates: [],
      offsetSelectedIds: [],
      offsetModalError: '',
      offsetSubmitting: false,
    };
    this.offsetDialogRef = React.createRef();
  }

  componentDidMount() {
    this.fetchDetail();
  }

  componentDidUpdate(prevProps, prevState) {
    // 相手先精算データのリンク等で URL の :id が変わったら詳細を再取得する。
    // 同一ルート(/private_money/:moneyId/clearings/:id)内の遷移ではコンポーネントが
    // 再マウントされず componentDidMount が走らないため、ここで追従しないと表示が
    // 前の精算データのまま残る（遷移していないように見える）。
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.setState({ clearing: {}, detail: {}, offsets: [] });
      this.fetchDetail();
      return;
    }

    // 相殺追加モーダルを開いた瞬間にダイアログへフォーカスを移す（キーボード操作の起点）。
    if (!prevState.offsetModalOpen && this.state.offsetModalOpen && this.offsetDialogRef.current) {
      this.offsetDialogRef.current.focus();
    }
  }

  // モーダル内のキーボード操作: Escape で閉じ、Tab はダイアログ内にフォーカスを閉じ込める（focus trap）。
  handleOffsetDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      this.closeOffsetModal();
      return;
    }
    if (e.key !== 'Tab') return;
    const dialog = this.offsetDialogRef.current;
    if (!dialog) return;
    const focusable = Array.from(
      dialog.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.disabled && el.offsetParent !== null);
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  fetchDetail() {
    // 相手先リンクで連続遷移した際、古い getClearing の応答が後着して新しい表示を
    // 上書きするのを防ぐ（最後に要求した id の応答だけを反映する）。
    const requestedId = this.props.match.params.id;
    this.latestRequestedId = requestedId;
    return getClearing(requestedId).then((clearingWithDetail) => {
      if (this.latestRequestedId !== requestedId) return;
      this.setState({
        clearing: clearingWithDetail.clearing,
        detail: clearingWithDetail.detail,
        offsets: clearingWithDetail.offsets ?? [],
      });
    });
  }

  // 相殺除外（確定前の offset 削除）・精算済化は発行体のみ許可。BE の認可と揃える。
  isIssuer() {
    return canSettleClearing(this.props.user);
  }

  // 予定相殺（未確定の相殺）が付いた「請求」データは直接精算できない（二重回収防止・BE も
  // clearing_invoice_has_pending_offset で拒否）。請求詳細の offsets は相手=支払であり、
  // confirmed=false はその支払が未確定＝予定相殺。clearing 自体には派生種別が無いため
  // 金額（invoice > payment ＝請求/債権）で種別を判定する。
  hasBlockingPendingOffset() {
    const { clearing, offsets } = this.state;
    const isInvoice = (clearing.invoice_amount ?? 0) > (clearing.payment_amount ?? 0);
    return isInvoice && (offsets || []).some((o) => !o.confirmed);
  }

  // 精算済化（不可逆）。発行体のみ。確認後に done_at をセットし、詳細を再取得する。
  // 支払の精算済化は確定相殺で残高0になった請求も連動 done になる（BE のカスケード）。
  handleSettle() {
    this.props.openAlertModal({
      body: (
        <div style={{ textAlign: 'left' }}>
          <p>この精算データを精算済みにします。</p>
          <p style={{ color: '#d75f48' }}>この操作は取り消せません。</p>
        </div>
      ),
      confirmText: '精算済にする',
      cancelText: 'キャンセル',
      confirmColor: 'danger',
      onConfirm: () => {
        updateClearings(this.props.match.params.id)
          .then(() => this.fetchDetail())
          .catch(() => {
            this.props.openAlertModal({
              body: '精算状態の更新に失敗しました。時間をおいて再度お試しください。',
              useCancel: false,
            });
          });
      },
    });
  }

  handleRemoveOffset(offsetId) {
    this.props.openAlertModal({
      body: (
        <div>
          <p>この相殺（債権への充当）を取り消してもよろしいでしょうか？</p>
          <p>実振込額が増え、債権は未回収のまま残ります。</p>
        </div>
      ),
      confirmText: '相殺を外す',
      onConfirm: () => {
        deleteClearingOffset(offsetId)
          .then(() => this.fetchDetail())
          .catch(() => {
            this.props.openAlertModal({
              body: '相殺の取り消しに失敗しました。確定済み等の理由で取り消せない可能性があります。',
              useCancel: false,
            });
          });
      },
    });
  }

  onClickTransactionDetail(transactionType) {
    const selectedMoneyId = this.props.match.params.moneyId;
    this.props.updateListForm({
      privateMoney: selectedMoneyId,
      transactionTypes: [transactionType],
      from: moment(this.state.clearing.from),
      to: moment(this.state.clearing.to),
      organization: this.state.clearing.claimed_organization.code,
    });
    this.props.history.push('/clearing/transactions');
  }

  // 「相殺する請求データを追加する」モーダルを開き、同一加盟店の未回収請求(債権)候補を取得する。
  // 候補は listing API を claimed_organization_code で絞って取得するため他加盟店は出ない。
  // この支払が既に相殺している請求は除外する（(payment,invoice) は一意で二重追加できないため。
  // 金額を直したい場合は既存の相殺を「相殺を外す」で外してから再追加する運用）。
  openOffsetModal = () => {
    // 閉じた後にトリガー要素へフォーカスを戻すため、開く直前の活性要素を控えておく（a11y）。
    this.triggerBeforeOffsetModal = document.activeElement;
    const { clearing, offsets } = this.state;
    const moneyId = this.props.match.params.moneyId;
    const memberCode = clearing.claimed_organization?.code;
    const alreadyOffsetIds = new Set(
      (offsets || []).map((o) => o.counterpart_clearing?.id).filter(Boolean)
    );
    this.setState({
      offsetModalOpen: true,
      offsetModalError: '',
      offsetSelectedIds: [],
      offsetCandidates: [],
    });
    const query = `clearing_type=invoice&status=pending&claimed_organization_code=${encodeURIComponent(
      memberCode || ''
    )}`;
    getClearings(moneyId, query)
      .then((rows) => {
        const candidates = (rows || [])
          .filter((r) => (r.receivable_balance ?? 0) > 0)
          .filter((r) => !alreadyOffsetIds.has(r.id));
        this.setState({ offsetCandidates: candidates });
      })
      .catch(() => this.setState({ offsetModalError: '候補の取得に失敗しました' }));
  };

  closeOffsetModal = () =>
    this.setState({ offsetModalOpen: false }, () => {
      // モーダルを開いたトリガー要素へフォーカスを戻す（キーボード/SR 利用者が迷子にならないように）。
      this.triggerBeforeOffsetModal?.focus?.();
    });

  submitOffset = () => {
    // 逐次 POST 中の二重起動を防ぐ（disabled 反映前の連打対策。BE の一意制約でも吸収されるが念のため）。
    if (this.state.offsetSubmitting) return;
    const { offsetSelectedIds, offsetCandidates, clearing } = this.state;
    if (offsetSelectedIds.length === 0) {
      this.setState({ offsetModalError: '相殺する債権を選択してください' });
      return;
    }
    // 締め日が古い順に処理し、選択した債権を支払の充当可能額の範囲で「全額相殺」する。
    // （部分相殺の任意金額入力はユースケースが不明瞭なため廃止。常に残高全額を充当。）
    const ordered = offsetCandidates
      .filter((c) => offsetSelectedIds.includes(c.id))
      .sort((a, b) => new Date(a.to) - new Date(b.to));
    let applied = 0;
    let stopMessage = '';
    this.setState({ offsetSubmitting: true, offsetModalError: '' });

    // 充当は支払の残額に依存して順序があるため、POST を逐次実行する（promise chain）。
    ordered
      .reduce(
        (chain, c) =>
          chain.then((stop) => {
            if (stop) return true;
            // amount を指定しない＝BE 側で min(充当可能額, 債権残高) を全額充当する。
            const payload = {
              payment_clearing_id: clearing.id,
              invoice_clearing_id: c.id,
            };
            return createClearingOffset(payload)
              .then(() => {
                applied += 1;
                return false;
              })
              .catch((errPromise) =>
                Promise.resolve(errPromise)
                  .catch(() => null)
                  .then((body) => {
                    // 充当可能額に達した場合は「ここまでで打ち切り」（部分相殺として正常終了）。
                    stopMessage =
                      body && body.type === 'clearing_offset_no_available_amount'
                        ? '支払の充当可能額に達したため、一部の債権のみ相殺予定に追加しました。'
                        : (body && body.message) || '相殺予定の追加に失敗しました。';
                    return true;
                  })
              );
          }),
        Promise.resolve(false)
      )
      .then(() => {
        this.setState({ offsetSubmitting: false });
        if (applied > 0) {
          this.setState({ offsetModalOpen: false });
          this.fetchDetail();
          if (stopMessage) {
            this.props.openAlertModal({ body: stopMessage, useCancel: false });
          }
        } else {
          this.setState({ offsetModalError: stopMessage || '相殺予定を追加できませんでした。' });
        }
      });
  };

  renderOffsetModal() {
    const {
      offsetModalOpen,
      offsetCandidates,
      offsetSelectedIds,
      offsetModalError,
      offsetSubmitting,
    } = this.state;
    if (!offsetModalOpen) return null;
    const memberName = this.state.clearing?.claimed_organization?.name ?? '';
    const hasCandidates = offsetCandidates.length > 0;
    const toggleCandidate = (id) =>
      this.setState((prev) => ({
        offsetSelectedIds: prev.offsetSelectedIds.includes(id)
          ? prev.offsetSelectedIds.filter((x) => x !== id)
          : [...prev.offsetSelectedIds, id],
      }));
    return (
      <ModalOverlay onClick={this.closeOffsetModal}>
        <ModalDialog
          ref={this.offsetDialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="相殺する請求データを追加する"
          tabIndex={-1}
          onKeyDown={this.handleOffsetDialogKeyDown}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHead>
            <Headings.h5 style={{ margin: 0 }}>相殺する請求データを追加する</Headings.h5>
            <ModalCloseButton type="button" aria-label="閉じる" onClick={this.closeOffsetModal}>
              ×
            </ModalCloseButton>
          </ModalHead>
          <ModalBodyWrap>
            {hasCandidates ? (
              <>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  {memberName}の未回収債権一覧
                </div>
                <div style={{ marginBottom: 8 }}>
                  この支払で相殺する債権を選択してください（複数選択可）。選択した債権は、支払の充当可能額の範囲で全額相殺されます。
                </div>
                <OffsetTable>
                  <thead>
                    <tr>
                      <th aria-label="選択" />
                      <th>対象期間</th>
                      <th>債権残高</th>
                      <th>予定相殺額</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offsetCandidates.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <input
                            type="checkbox"
                            name="offset-candidate"
                            checked={offsetSelectedIds.includes(c.id)}
                            onChange={() => toggleCandidate(c.id)}
                          />
                        </td>
                        <td>
                          {`${moment(c.from).format('YYYY/MM/DD')} ~ ${moment(c.to).format(
                            'YYYY/MM/DD'
                          )}`}
                        </td>
                        <td className="amount">
                          {(c.receivable_balance ?? 0).toLocaleString()} 円
                        </td>
                        <td className="amount">
                          {(c.pending_offset_amount ?? 0).toLocaleString()} 円
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </OffsetTable>
              </>
            ) : (
              <div>相殺できる未回収債権がありません。</div>
            )}
            {offsetModalError && (
              <div style={{ color: '#d75f48', marginTop: 8 }}>{offsetModalError}</div>
            )}
          </ModalBodyWrap>
          <ModalFoot>
            <Button variant="muted-outline" onClick={this.closeOffsetModal}>
              キャンセル
            </Button>
            <Button
              onClick={this.submitOffset}
              disabled={offsetSubmitting || !hasCandidates || offsetSelectedIds.length === 0}
            >
              追加する
            </Button>
          </ModalFoot>
        </ModalDialog>
      </ModalOverlay>
    );
  }

  renderOffsetSection() {
    const { clearing, offsets } = this.state;
    const {
      clearing_type: clearingType,
      pre_offset_amount: preOffsetAmount,
      offset_amount: offsetAmount,
      pending_offset_amount: pendingOffsetAmount,
      actual_transfer_amount: actualTransferAmount,
      receivable_balance: receivableBalance,
      offset_status: offsetStatus,
      done_at: doneAt,
    } = clearing;

    const moneyId = this.props.match.params.moneyId;
    const isPayment = clearingType === 'payment';
    // 相殺が実在しない（OFF マネー等）場合は従来表示を変えないため何も描画しない。
    // ただし「支払・確定前・発行体」のときは手動相殺付与の導線を出すため表示する。
    const hasOffsetInfo =
      (offsets && offsets.length > 0) || (offsetAmount ?? 0) > 0 || (pendingOffsetAmount ?? 0) > 0;
    const canAddOffset = isPayment && !doneAt && this.isIssuer();
    if (!hasOffsetInfo && !canAddOffset) return null;

    // 上の「合計」セクションと同じグリッドで1行を描画する（ラベル=太字 SubHeading / 金額=通常太さ・右寄せ）。
    const ladderRow = (label, value, extra = null) => (
      <>
        <Grid.Item nextRow size={2} style={{ marginTop: '0.5em' }}>
          <SubHeading>{label}</SubHeading>
        </Grid.Item>
        <Grid.Item size={4} justifyContent="end" style={{ marginTop: '0.5em', gap: '8px' }}>
          <span>{value}</span>
          {extra}
        </Grid.Item>
      </>
    );

    return (
      <OffsetSection>
        <GridWrapper>
          <Grid.Item nextRow size={6}>
            <Headings.h5 style={{ margin: 0 }}>債権相殺</Headings.h5>
          </Grid.Item>
          {hasOffsetInfo &&
            (isPayment ? (
              <>
                {ladderRow('相殺前精算額', `${formatNum(preOffsetAmount ?? 0)} 円`)}
                {ladderRow('債権相殺額', `− ${formatNum(offsetAmount)} 円`)}
                {ladderRow(
                  `実振込額（${doneAt ? '確定' : '予定'}）`,
                  `${formatNum(actualTransferAmount)} 円`
                )}
              </>
            ) : (
              <>
                {ladderRow('新規債権発生額', `${formatNum(Math.abs(preOffsetAmount ?? 0))} 円`)}
                {ladderRow('確定相殺額', `${formatNum(offsetAmount)} 円`)}
                {ladderRow('予定相殺額', `${formatNum(pendingOffsetAmount ?? 0)} 円`)}
                {ladderRow(
                  '債権残高',
                  `${formatNum(receivableBalance)} 円`,
                  offsetStatus && <OffsetStatusBadge status={offsetStatus} />
                )}
              </>
            ))}
        </GridWrapper>

        {offsets && offsets.length > 0 && (
          <>
            <OffsetCaption>
              {isPayment
                ? 'この支払で相殺した債権（相手＝請求データ）の一覧です。'
                : 'この債権を相殺している支払（相手＝支払データ）の一覧です。確定＝相手支払が精算済、予定＝未精算。'}
            </OffsetCaption>
            <OffsetTable>
            <thead>
              <tr>
                <th>相手先精算データ</th>
                <th>種別</th>
                <th>対象期間</th>
                <th>充当額</th>
                <th>状態</th>
                {this.isIssuer() && <th>操作</th>}
              </tr>
            </thead>
            <tbody>
              {offsets.map((o) => {
                const counterpart = o.counterpart_clearing ?? {};
                return (
                  <tr key={o.id}>
                    <td>
                      <Link to={`/private_money/${moneyId}/clearings/${counterpart.id}`}>
                        {counterpart.claimed_organization?.name ?? '—'}
                      </Link>
                    </td>
                    <td>{counterpart.clearing_type === 'payment' ? '支払' : '請求'}</td>
                    <td>
                      {counterpart.from
                        ? `${moment(counterpart.from).format('YYYY/MM/DD')} ~ ${moment(
                            counterpart.to
                          ).format('YYYY/MM/DD')}`
                        : '—'}
                    </td>
                    <td className="amount">{formatNum(o.amount)} 円</td>
                    <td>{o.confirmed ? '確定' : '予定'}</td>
                    {this.isIssuer() && (
                      <td>
                        {!o.confirmed && (
                          <Button
                            variant="muted-outline"
                            aria-label={`相殺を外す（${counterpart.claimed_organization?.name ?? ''}）`}
                            onClick={() => this.handleRemoveOffset(o.id)}
                          >
                            相殺を外す
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            </OffsetTable>
          </>
        )}
        {canAddOffset && (
          <div style={{ marginTop: 12 }}>
            {/* 実振込額（=残りの充当可能額）が0以下＝支払額をすべて相殺済みなら、
                これ以上相殺できないのでボタンを不活性化する。 */}
            <Button
              onClick={this.openOffsetModal}
              disabled={(actualTransferAmount ?? 0) <= 0}
            >
              相殺する請求データを追加する
            </Button>
          </div>
        )}
        {this.renderOffsetModal()}
      </OffsetSection>
    );
  }

  render() {
    const crumb = [
      { value: 'ホーム', href: '/', active: false },
      { value: '精算管理', href: '', active: true },
      { value: '精算データ一覧', href: '/clearings', active: false },
      { value: '精算データ詳細', href: '', active: true },
    ];

    const {
      claimed_organization,
      from,
      to,
      invoice_amount,
      consumption_tax_amount,
      payment_amount,
      payment_fee_rate_percentage,
      system_fee,
      fixed_fee,
      issuer_organization_name,
      issuer_invoice_registration_number,
    } = this.state.clearing;

    const {
      payment_money_amount,
      payment_money_count,
      payment_point_amount,
      payment_point_count,
      payment_fee_amount,
      topup_amount,
      topup_count,
      refund_payment_money_amount,
      refund_payment_money_count,
      refund_payment_point_amount,
      refund_payment_point_count,
      refund_topup_amount,
      refund_topup_count,
      bear_point_amount,
      bear_point_count,
      refund_bear_point_amount,
      refund_bear_point_count,
      system_fee_count,
      fixed_fee_count = fixed_fee === 0 ? 0 : 1,
      transfer_payment_amount,
      transfer_invoice_amount,
      other_clearing_amount,
    } = this.state.detail;

    const loading = Object.keys(this.state.clearing).length === 0;

    return (
      <PageWrapper>
        <Breadcrumb items={crumb} />
        <DetailContent>
          <Headings.h5>精算データ詳細</Headings.h5>
          {!loading ? (
            <GridWrapper>
              <Grid.Item startColumn={10} size={2} flexDirection="column">
                <Headings.h5>{issuer_organization_name}</Headings.h5>
                <Headings.h5>
                  登録番号{' '}
                  {!!issuer_invoice_registration_number && issuer_invoice_registration_number}
                </Headings.h5>
              </Grid.Item>
              <Grid.Item size={6} flexDirection="column" nextRow>
                <Headings.h5>
                  決済加盟店名：
                  {claimed_organization !== undefined ? claimed_organization.name : ''}
                </Headings.h5>
                <div>
                  <Headings.h5>対象期間：</Headings.h5>
                  {`${moment(from).format('YYYY/MM/DD')} ~ ${moment(to).format('YYYY/MM/DD')}`}
                </div>
                <div>
                  <Headings.h5>精算状態：</Headings.h5>
                  {this.state.clearing.done_at ? '精算済' : '未精算'}
                  {!this.state.clearing.done_at &&
                    this.isIssuer() &&
                    (this.hasBlockingPendingOffset() ? (
                      <PendingOffsetNote>
                        予定相殺があるため精算できません。下の「債権相殺」で相殺を外してから精算してください。
                      </PendingOffsetNote>
                    ) : (
                      <DetailSettleButton type="button" onClick={() => this.handleSettle()}>
                        精算済にする
                      </DetailSettleButton>
                    ))}
                </div>
              </Grid.Item>
              <Grid.Item
                size={6}
                alignItems="center"
                style={{ flexDirection: 'row-reverse', marginRight: '16px' }}
              >
                {/* 支払通知書は「支払」種別のみ(発行体→加盟店の支払のお知らせ)。 */}
                {isPaymentClearing(this.state.clearing) && (
                  <PaymentNoticeDownloadButton
                    clearingId={this.props.match.params.id}
                    privateMoneyId={this.props.match.params.moneyId}
                    label="支払通知書"
                  />
                )}
              </Grid.Item>

              <Grid.Divider />

              {/* topup-transfer-row */}
              <Grid.Item nextRow size={6}>
                <Headings.h5>取引</Headings.h5>
              </Grid.Item>
              <Grid.Item nextRow size={2}>
                <SubHeading>チャージ取引</SubHeading>
              </Grid.Item>
              <Grid.Item size={2} flexDirection="column" gap="0.5em">
                <Cell>{topup_count} 件</Cell>
                <Cell>キャンセル：{refund_topup_count || 0} 件</Cell>
              </Grid.Item>
              <Grid.Item size={2} flexDirection="column" gap="0.5em">
                <AmountCell amount={topup_amount || 0} />
                <AmountCell amount={refund_topup_amount || 0} />
              </Grid.Item>
              <Grid.Item size={6}>
                <TransactionDetailButton
                  onClick={this.onClickTransactionDetail.bind(this, 'topup')}
                  disabled={isNullOrUndifined(claimed_organization)}
                >
                  内訳
                </TransactionDetailButton>
              </Grid.Item>

              <Grid.Item nextRow size={2} style={{ marginTop: '0.5em' }}>
                <SubHeading>チャージ請求額</SubHeading>
              </Grid.Item>
              <Grid.Item size={4} justifyContent="end" style={{ marginTop: '0.5em' }}>
                <AmountCell amount={(topup_amount || 0) - (refund_topup_amount || 0)} />
              </Grid.Item>

              <Grid.Divider />

              {/* payment-transfer-row */}
              <Grid.Item nextRow size={2}>
                <SubHeading>決済取引</SubHeading>
              </Grid.Item>
              <Grid.Item size={2} flexDirection="column" gap="0.5em">
                <Cell>マネー：{payment_money_count || 0} 件</Cell>
                <Cell>ポイント：{payment_point_count || 0} 件</Cell>
                <Cell>マネーキャンセル：{refund_payment_money_count || 0} 件</Cell>
                <Cell>ポイントキャンセル：{refund_payment_point_count || 0} 件</Cell>
              </Grid.Item>
              <Grid.Item size={2} flexDirection="column" gap="0.5em">
                <AmountCell amount={payment_money_amount || 0} />
                <AmountCell amount={payment_point_amount || 0} />
                <AmountCell amount={refund_payment_money_amount || 0} />
                <AmountCell amount={refund_payment_point_amount || 0} />
              </Grid.Item>
              <Grid.Item size={6}>
                <TransactionDetailButton
                  onClick={this.onClickTransactionDetail.bind(this, 'payment')}
                  disabled={isNullOrUndifined(claimed_organization)}
                >
                  内訳
                </TransactionDetailButton>
              </Grid.Item>

              <Grid.Item nextRow size={2} style={{ marginTop: '0.5em' }}>
                <SubHeading>決済支払額</SubHeading>
              </Grid.Item>
              <Grid.Item size={4} justifyContent="end" style={{ marginTop: '0.5em' }}>
                <AmountCell amount={transfer_payment_amount || 0} />
              </Grid.Item>

              <Grid.Divider />

              {/* payment-clearing-row */}
              <Grid.Item nextRow size={2}>
                <SubHeading>決済手数料</SubHeading>
              </Grid.Item>
              <Grid.Item size={2} justifyContent="end">
                <Cell>{payment_fee_rate_percentage || 0} %</Cell>
              </Grid.Item>
              <Grid.Item size={2} justifyContent="end">
                <AmountCell amount={payment_fee_amount || 0} />
              </Grid.Item>

              <Grid.Divider />

              {/* bear-point-row */}
              <Grid.Item nextRow size={2}>
                <SubHeading>ポイント負担取引</SubHeading>
              </Grid.Item>
              <Grid.Item size={2} flexDirection="column" gap="0.5em">
                <Cell>決済：{bear_point_count || 0} 件</Cell>
                <Cell>決済キャンセル：{refund_bear_point_count || 0} 件</Cell>
              </Grid.Item>
              <Grid.Item size={2} flexDirection="column" gap="0.5em">
                <AmountCell amount={bear_point_amount || 0} />
                <AmountCell amount={refund_bear_point_amount || 0} />
              </Grid.Item>

              <Grid.Item nextRow size={2} style={{ marginTop: '0.5em' }}>
                <SubHeading>ポイント負担請求額</SubHeading>
              </Grid.Item>
              <Grid.Item size={4} justifyContent="end" style={{ marginTop: '0.5em' }}>
                <AmountCell amount={(bear_point_amount || 0) - (refund_bear_point_amount || 0)} />
              </Grid.Item>

              <Grid.Divider />

              {/* summary */}
              <Grid.Item nextRow size={2}>
                <SubHeading>取引請求額</SubHeading>
              </Grid.Item>
              <Grid.Item size={4} justifyContent="end">
                <AmountCell amount={transfer_invoice_amount || 0} />
              </Grid.Item>
              <Grid.Item size={6}>
                <FormulaText>チャージ請求額 + 決済手数料 + ポイント負担請求額</FormulaText>
              </Grid.Item>

              <Grid.Item nextRow size={2} style={{ marginTop: '0.5em' }}>
                <SubHeading>取引支払額</SubHeading>
              </Grid.Item>
              <Grid.Item size={4} justifyContent="end" style={{ marginTop: '0.5em' }}>
                <AmountCell amount={transfer_payment_amount || 0} />
              </Grid.Item>
              <Grid.Item
                size={6}
                flexDirection="column"
                gap={'0.5em'}
                style={{ marginTop: '0.5em' }}
              >
                <FormulaText>請求額</FormulaText>
                <FormulaText>決済支払額</FormulaText>
              </Grid.Item>

              <Grid.Divider />

              {/* other-fee */}
              <Grid.Item nextRow size={6}>
                <Headings.h5>その他</Headings.h5>
              </Grid.Item>
              <Grid.Item nextRow size={2}>
                <SubHeading>月額システム利用料</SubHeading>
              </Grid.Item>
              <Grid.Item size={1} justifyContent="end">
                <Cell>{formatNum(system_fee)} 円 / 件</Cell>
              </Grid.Item>
              <Grid.Item size={1} justifyContent="end">
                <Cell>{formatNum(system_fee_count)} 件</Cell>
              </Grid.Item>
              <Grid.Item size={2} justifyContent="end">
                <AmountCell amount={system_fee * system_fee_count} />
              </Grid.Item>

              <Grid.Divider />

              <Grid.Item nextRow size={2}>
                <SubHeading>その他固定費</SubHeading>
              </Grid.Item>
              <Grid.Item size={1} justifyContent="end">
                <Cell>{formatNum(fixed_fee)} 円</Cell>
              </Grid.Item>
              <Grid.Item size={1} justifyContent="end">
                <Cell>{formatNum(fixed_fee_count)} 件</Cell>
              </Grid.Item>
              <Grid.Item size={2} justifyContent="end">
                <AmountCell amount={fixed_fee * fixed_fee_count} />
              </Grid.Item>

              <Grid.Divider />

              <Grid.Item nextRow size={2}>
                <SubHeading>その他請求額（税込）</SubHeading>
              </Grid.Item>
              <Grid.Item size={4} justifyContent="end">
                <AmountCell amount={other_clearing_amount} />
              </Grid.Item>

              <Grid.Divider />

              {/* totals */}
              <Grid.Item nextRow size={6}>
                <Headings.h5>合計</Headings.h5>
              </Grid.Item>
              <Grid.Item nextRow size={2} style={{ marginTop: '0.5em' }}>
                <SubHeading>請求金額合計</SubHeading>
              </Grid.Item>
              <Grid.Item size={4} justifyContent="end" style={{ marginTop: '0.5em' }}>
                <AmountCell amount={invoice_amount || 0} />
              </Grid.Item>
              <Grid.Item size={6} style={{ marginTop: '0.5em' }}>
                <FormulaText>取引請求額 + その他請求額</FormulaText>
              </Grid.Item>

              <Grid.Item nextRow size={2} style={{ marginTop: '0.5em' }}>
                <SubHeading>うち消費税額（10％対象）</SubHeading>
              </Grid.Item>
              <Grid.Item size={4} justifyContent="end" style={{ marginTop: '0.5em' }}>
                <AmountCell amount={consumption_tax_amount || 0} />
              </Grid.Item>

              <Grid.Item nextRow size={2} style={{ marginTop: '0.5em' }}>
                <SubHeading>合計精算額</SubHeading>
              </Grid.Item>
              <Grid.Item size={3} justifyContent="end" style={{ marginTop: '0.5em' }}>
                <div>
                  <ClearingBadge clearing={this.state.clearing} />
                </div>
              </Grid.Item>
              <Grid.Item size={1} justifyContent="end" style={{ marginTop: '0.5em' }}>
                <ClearingCell amount={invoice_amount - payment_amount} />
              </Grid.Item>
              <Grid.Item size={6} style={{ marginTop: '0.5em' }}>
                <FormulaText>取引支払額 - 請求金額合計</FormulaText>
              </Grid.Item>

              <Grid.Divider />
            </GridWrapper>
          ) : (
            <LoaderWrapper>
              <Loader type="Oval" width={50} height={50} />
            </LoaderWrapper>
          )}
          {!loading && this.renderOffsetSection()}
        </DetailContent>
      </PageWrapper>
    );
  }
}

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps, { updateListForm, openAlertModal })(
  CSSModules(ClearingDetail, styles, { allowMultiple: true })
);
